import { NotificationChannel } from '@frontend/common';
import { TextInput } from '@frontend/components/basic-forms';
import { insertTextAtSelection, useHtmlTemplateEditor } from '@frontend/html-template-editor';
import { HtmlBodyEditor, HtmlSourceValidationPanel, HtmlTemplateDraftBanner } from '@frontend/html-template-editor/editor';
import { NotificationContent } from '@frontend/notification/types';
import type * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { ContentPreview } from './content-preview/content-preview.component';
import { PreviewButton } from './editor-toolbar-controls';
import { useEditor } from './editor.controller';
import { FooterBar } from './footer-bar/footer-bar.component';

export interface NotificationContentEditorProps {
    content: NotificationContent;
    placeholders: string[];
    readOnly?: boolean;
    onCancel(): void;
}

export const NotificationContentEditor = (props: NotificationContentEditorProps) => {
    if (props.readOnly) {
        return <ReadOnlyPreview content={props.content} />;
    }

    return <EditableNotificationContent {...props} />;
};

const EditableNotificationContent = (props: NotificationContentEditorProps) => {
    const viewProps = useEditor(props);
    const showSubjectLengthWarning = viewProps.subject.length > 78;
    const isRichEditor = props.content.channel === NotificationChannel.EMAIL;

    return (
        <div className='bg-background-surface outline-border-default flex h-full w-full flex-col items-start justify-start self-stretch rounded-xl outline-1 outline-offset-[-1px]'>
            <div className='inline-flex flex-col items-start justify-start gap-6 self-stretch rounded-tl-2xl rounded-tr-2xl px-4 pt-4 pb-3'>
                <div className='text-foreground-default justify-start text-2xl font-semibold'>
                    <FormattedMessage
                        id='content-detail.message'
                        description='Heading above the notification content editor'
                        defaultMessage='Message'
                    />
                </div>
                <TextInput
                    label={
                        <FormattedMessage
                            id='content-detail.subject'
                            description='Label for a notification content subject'
                            defaultMessage='Subject'
                        />
                    }
                    value={viewProps.subject}
                    onChange={viewProps.setSubject}
                />
                {showSubjectLengthWarning && (
                    <div className='text-status-warning text-sm font-medium'>
                        <FormattedMessage
                            id='content-detail.editor.subject-length-warning'
                            description='Warning shown when notification email subject may be too long for inbox previews'
                            defaultMessage='Long subjects may be truncated in inbox previews.'
                        />
                    </div>
                )}
                <div className='text-foreground-default justify-start text-center text-base font-medium'>
                    <FormattedMessage
                        id='content-detail.body'
                        description='Label above the notification content body editor'
                        defaultMessage='Body'
                    />
                </div>
            </div>
            {viewProps.recoverableDraft && (
                <HtmlTemplateDraftBanner
                    timestamp={viewProps.recoverableDraft.timestamp}
                    onRestore={viewProps.restoreDraft}
                    onDiscard={viewProps.discardDraft}
                />
            )}
            <TestSendPanel />
            {!isRichEditor ? (
                <>
                    <PlainTextToolbar
                        content={props.content}
                        subject={viewProps.subject}
                        body={viewProps.body}
                        isDirty={viewProps.isDirty}
                    />
                    <PlainTextEditor
                        value={viewProps.body}
                        onChange={viewProps.setBody}
                    />
                </>
            ) : (
                <HtmlBodyEditor
                    value={viewProps.body}
                    onChange={viewProps.setBody}
                    mode={viewProps.mode}
                    onModeChange={viewProps.changeMode}
                    onFormatSource={viewProps.formatSource}
                    editorKey={`${props.content.id}-${viewProps.editorKey}`}
                    enableEmailRegions
                    toolbarEnd={
                        <PreviewButton
                            content={props.content}
                            subject={viewProps.subject}
                            body={viewProps.body}
                            isDirty={viewProps.isDirty}
                        />
                    }
                />
            )}
            <HtmlSourceValidationPanel messages={viewProps.validationMessages} />
            <FooterBar
                isDirty={viewProps.isDirty}
                isSaving={viewProps.isSaving}
                hasValidationErrors={viewProps.validationErrors.length > 0}
                onReset={viewProps.resetContent}
                onSave={viewProps.save}
                onCancel={viewProps.discardChanges}
            />
        </div>
    );
};

const PlainTextToolbar = (props: { content: NotificationContent; subject: string; body: string; isDirty: boolean }) => (
    <div className='outline-border-default inline-flex items-center justify-between self-stretch p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-brand-muted text-sm font-medium'>
            <FormattedMessage
                id='content-detail.editor.plain-text-mode'
                description='Mode label shown when editing a non-email notification content body as plain text'
                defaultMessage='Plain text'
            />
        </div>
        <PreviewButton
            content={props.content}
            subject={props.subject}
            body={props.body}
            isDirty={props.isDirty}
        />
    </div>
);

const ReadOnlyPreview = (props: { content: NotificationContent }) => (
    <ContentPreview
        content={props.content}
        draft={null}
        onClose={() => undefined}
        showClose={false}
    />
);

const PlainTextEditor = (props: { value: string; onChange(_value: string): void }) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const { registerInsertHandler } = useHtmlTemplateEditor();
    const valueRef = useRef(props.value);
    const onChangeRef = useRef(props.onChange);
    valueRef.current = props.value;
    onChangeRef.current = props.onChange;

    const insertAtSelection = useCallback((text: string) => {
        const target = textareaRef.current;
        if (!target) return;

        const { next, cursor } = insertTextAtSelection(valueRef.current, text, target.selectionStart, target.selectionEnd);
        onChangeRef.current(next);
        requestAnimationFrame(() => {
            target.selectionStart = cursor;
            target.selectionEnd = cursor;
            target.focus();
        });
    }, []);

    useEffect(() => {
        registerInsertHandler(insertAtSelection);
        return () => registerInsertHandler(null);
    }, [insertAtSelection, registerInsertHandler]);

    const insertDroppedText = (event: React.DragEvent<HTMLTextAreaElement>) => {
        event.preventDefault();
        const text = event.dataTransfer.getData('text/plain') || event.dataTransfer.getData('text/html');
        if (!text) return;
        insertAtSelection(text);
    };

    return (
        <div className='mx-4 flex min-h-96 flex-col self-stretch'>
            <label
                className='text-brand-muted mb-2 text-sm font-medium'
                htmlFor='notification-plain-text-body'>
                <FormattedMessage
                    id='content-detail.editor.plain-text-body'
                    description='Label above the plain text body field for non-email notification content'
                    defaultMessage='Plain text body'
                />
            </label>
            <textarea
                id='notification-plain-text-body'
                ref={textareaRef}
                className='border-border-default text-foreground-default focus:border-brand-primary min-h-96 resize-y rounded-md border bg-white p-3 text-sm leading-6 outline-none'
                value={props.value}
                spellCheck={true}
                onChange={(event) => props.onChange(event.target.value)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={insertDroppedText}
            />
        </div>
    );
};

const TEST_SEND_ENABLED = false;

const TestSendPanel = () => {
    if (!TEST_SEND_ENABLED) return null;

    return (
        <div className='bg-background-default mx-4 mb-3 rounded-md px-3 py-2 text-sm'>
            <FormattedMessage
                id='content-detail.editor.test-send-disabled'
                description='Hidden test-send panel copy shown once backend support for notification test sends is enabled'
                defaultMessage='Send a test notification'
            />
        </div>
    );
};
